import React from 'react';
import { Check, Clock, AlertTriangle, X, Pause } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ProjectStatus, ProjectStage } from '../../types';

interface ProjectStatusTimelineProps {
  currentStatus: ProjectStatus;
  stages: ProjectStage[];
  className?: string;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

const STATUS_ORDER: ProjectStatus[] = [
  ProjectStatus.A_PREPARER,
  ProjectStatus.SOUMIS,
  ProjectStatus.ACCORD_PROVISOIRE,
  ProjectStatus.VALIDE,
  ProjectStatus.DEBLOCAGE_FONDS,
  ProjectStatus.EN_COURS,
  ProjectStatus.VERIFICATION_FINALE,
  ProjectStatus.TERMINE,
  ProjectStatus.REJETE,
  ProjectStatus.SUSPENDU
];

const STATUS_LABELS: Record<ProjectStatus, string> = {
  [ProjectStatus.A_PREPARER]: 'À préparer',
  [ProjectStatus.SOUMIS]: 'Soumis',
  [ProjectStatus.ACCORD_PROVISOIRE]: 'Accord provisoire',
  [ProjectStatus.VALIDE]: 'Validé',
  [ProjectStatus.DEBLOCAGE_FONDS]: 'Déblocage fonds',
  [ProjectStatus.EN_COURS]: 'En cours',
  [ProjectStatus.VERIFICATION_FINALE]: 'Vérification finale',
  [ProjectStatus.TERMINE]: 'Terminé',
  [ProjectStatus.REJETE]: 'Rejeté',
  [ProjectStatus.SUSPENDU]: 'Suspendu'
};

export const ProjectStatusTimeline: React.FC<ProjectStatusTimelineProps> = ({
  currentStatus,
  stages,
  className = '',
  fullscreen = false,
  onToggleFullscreen
}) => {
  const [hoveredStep, setHoveredStep] = React.useState<ProjectStatus | null>(null);

  const getStageInfo = (status: ProjectStatus) => {
    return stages.find(stage => stage.status === status);
  };

  const getStepState = (status: ProjectStatus, index: number) => {
    const stage = getStageInfo(status);
    const currentIndex = STATUS_ORDER.indexOf(currentStatus);
    
    // Special handling for terminal states
    if (status === ProjectStatus.REJETE || status === ProjectStatus.SUSPENDU) {
      return stage ? 'blocked' : 'upcoming';
    }
    
    if (stage) return 'done';
    if (index === currentIndex) return 'current';
    if (index < currentIndex) return 'skipped';
    return 'upcoming';
  };

  const getStepIcon = (status: ProjectStatus, state: string) => {
    switch (state) {
      case 'done':
        return <Check className="w-4 h-4 text-white" />;
      case 'current':
        return <Clock className="w-4 h-4 text-white" />;
      case 'blocked':
        if (status === ProjectStatus.REJETE) {
          return <X className="w-4 h-4 text-white" />;
        }
        return <Pause className="w-4 h-4 text-white" />;
      default:
        return <div className="w-2 h-2 bg-white rounded-full" />;
    }
  };

  const getStepColor = (state: string) => {
    switch (state) {
      case 'done':
        return 'bg-green-500 border-green-500';
      case 'current':
        return 'bg-blue-500 border-blue-500';
      case 'blocked':
        return 'bg-red-500 border-red-500';
      case 'skipped':
        return 'bg-gray-400 border-gray-400';
      default:
        return 'bg-gray-200 border-gray-300';
    }
  };

  const getConnectorColor = (fromState: string, toState: string) => {
    if (fromState === 'done' && (toState === 'done' || toState === 'current')) {
      return 'bg-green-500';
    }
    if (fromState === 'done' && toState === 'upcoming') {
      return 'bg-gray-300';
    }
    return 'bg-gray-300';
  };

  // Filter out terminal states from main flow, show them separately if active
  const mainFlow = STATUS_ORDER.filter(status => 
    status !== ProjectStatus.REJETE && status !== ProjectStatus.SUSPENDU
  );
  
  const terminalStates = [ProjectStatus.REJETE, ProjectStatus.SUSPENDU];
  const activeTerminalState = terminalStates.find(status => getStageInfo(status));

  return (
    <div className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 transition-all duration-300 ${fullscreen ? 'fixed inset-4 z-50 overflow-auto animate-scale-in' : 'animate-slide-up'} ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200">Frise d'avancement</h3>
        {onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 hover:scale-110"
            title={fullscreen ? 'Quitter le mode plein écran' : 'Mode plein écran'}
          >
            {fullscreen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>
        )}
      </div>
      
      {/* Main Timeline */}
      <div className="relative">
        {/* Desktop: Horizontal Timeline */}
        <div className="hidden lg:block">
          <div className="flex items-center justify-between relative animate-fade-in">
            {/* Background line */}
            <div className="absolute top-6 left-6 right-6 h-1 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 dark:from-gray-600 dark:via-gray-500 dark:to-gray-600 rounded-full" />
            
            {mainFlow.map((status, index) => {
              const stage = getStageInfo(status);
              const state = getStepState(status, index);
              
              return (
                <div 
                  key={status} 
                  className="relative flex flex-col items-center group"
                  onMouseEnter={() => setHoveredStep(status)}
                  onMouseLeave={() => setHoveredStep(null)}
                >
                  {/* Step Circle */}
                  <div 
                    className={`
                      relative z-10 w-14 h-14 rounded-full border-3 flex items-center justify-center
                      transition-all duration-300 cursor-pointer hover:scale-125 hover:shadow-2xl
                      ${getStepColor(state)} ${hoveredStep === status ? 'animate-bounce-subtle shadow-2xl' : ''}
                    `}
                    title={stage ? `${STATUS_LABELS[status]} - ${format(new Date(stage.reached_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}` : STATUS_LABELS[status]}
                  >
                    <div className={`transition-all duration-200 ${hoveredStep === status ? 'scale-110' : ''}`}>
                      {getStepIcon(status, state)}
                    </div>
                  </div>
                  
                  {/* Label */}
                  <div className="mt-4 text-center max-w-28">
                    <p className={`text-sm font-medium ${
                      state === 'done' ? 'text-green-700 dark:text-green-400' :
                      state === 'current' ? 'text-blue-700 dark:text-blue-400' :
                      'text-gray-600 dark:text-gray-400'
                    } transition-all duration-200 ${hoveredStep === status ? 'font-bold scale-105' : ''}`}>
                      {STATUS_LABELS[status]}
                    </p>
                    {stage && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-200">
                        {format(new Date(stage.reached_at), 'dd/MM/yyyy', { locale: fr })}
                      </p>
                    )}
                  </div>
                  
                  {/* Tooltip */}
                  {stage && (
                    <div className="absolute bottom-full mb-4 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-gray-900/95 dark:bg-gray-700/95 backdrop-blur-md text-white text-xs rounded-xl p-4 whitespace-nowrap z-20 shadow-2xl animate-slide-down">
                      <div className="space-y-1">
                        <p className="font-medium">{STATUS_LABELS[status]}</p>
                        <p>Le {format(new Date(stage.reached_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}</p>
                        {stage.set_by_name && <p>Par {stage.set_by_name}</p>}
                        {stage.note && <p className="italic">"{stage.note}"</p>}
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-6 border-transparent border-t-gray-900/95 dark:border-t-gray-700/95" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: Vertical Timeline */}
        <div className="lg:hidden space-y-4 animate-fade-in">
          {mainFlow.map((status, index) => {
            const stage = getStageInfo(status);
            const state = getStepState(status, index);
            
            return (
              <div key={status} className="flex items-center space-x-4 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                {/* Step Circle */}
                <div className={`
                  w-12 h-12 rounded-full border-3 flex items-center justify-center flex-shrink-0 transition-all duration-300 hover:scale-110 hover:shadow-lg
                  ${getStepColor(state)}
                `}>
                  {getStepIcon(status, state)}
                </div>
                
                {/* Content */}
                <div className="flex-1 transition-all duration-200 hover:translate-x-1">
                  <div className="flex items-center justify-between">
                    <h4 className={`font-medium ${
                      state === 'done' ? 'text-green-700 dark:text-green-400' :
                      state === 'current' ? 'text-blue-700 dark:text-blue-400' :
                      'text-gray-600 dark:text-gray-400'
                    } transition-colors duration-200`}>
                      {STATUS_LABELS[status]}
                    </h4>
                    {stage && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-200">
                        {format(new Date(stage.reached_at), 'dd/MM/yyyy', { locale: fr })}
                      </span>
                    )}
                  </div>
                  {stage?.note && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 italic transition-colors duration-200">"{stage.note}"</p>
                  )}
                  {stage?.set_by_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-200">Mis à jour par {stage.set_by_name}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Terminal State (if active) */}
      {activeTerminalState && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 animate-slide-up">
          <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border border-red-200 dark:border-red-800 rounded-xl animate-pulse hover:shadow-lg transition-all duration-300">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
              {activeTerminalState === ProjectStatus.REJETE ? (
                <X className="w-4 h-4 text-white" />
              ) : (
                <Pause className="w-4 h-4 text-white" />
              )}
            </div>
            <div className="flex-1 transition-all duration-200">
              <h4 className="font-medium text-red-900 dark:text-red-400 transition-colors duration-200">
                {STATUS_LABELS[activeTerminalState]}
              </h4>
              {getStageInfo(activeTerminalState) && (
                <div className="text-sm text-red-700 dark:text-red-400 mt-1 transition-colors duration-200">
                  <p>
                    Le {format(new Date(getStageInfo(activeTerminalState)!.reached_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </p>
                  {getStageInfo(activeTerminalState)?.set_by_name && (
                    <p>Par {getStageInfo(activeTerminalState)?.set_by_name}</p>
                  )}
                  {getStageInfo(activeTerminalState)?.note && (
                    <p className="italic mt-1">"{getStageInfo(activeTerminalState)?.note}"</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};